import { ICompileService } from "angular";
import { startCase } from "lodash";
import { CellDoubleClickedEventHandler } from "../cells/doubleClickHandlerService";
import {
  booleanValueGetterFactory,
  dateTimeValueGetter,
  dateValueGetter,
  ValueGetter,
} from "../cells/valueGetters";
import { ColDef } from "./colDef";
import { FilterProvider } from "./filters/FilterProvider";

export const vsName = "columnDefinitionService";

export interface PermissionColDef {
  requiresPermission?: string;
}

export interface simpleColDef extends PermissionColDef {
  field: string;
  header?: string;
  valueGetter?: ValueGetter;
  suppressMenu?: boolean;
  suppressSorting?: boolean;
  hide?: boolean;
  filter?: Function;
  cellDoubleClicked?: CellDoubleClickedEventHandler;
}

export interface commandColDef extends PermissionColDef {
  headerName: string;
  name: string;
  iconClass: string;
  width?: number;
  colId?: string;
}

export interface selectColDef extends PermissionColDef {
  headerName?: string;
}

export interface linkColDef extends PermissionColDef {
  width?: number;
  colId: string;
  iconClass: string;
  stateExpression: string;
  headerName: string;
}

export class ColumnDefinitionService {
  static $inject = ["$compile"];
  constructor(private readonly $compile: ICompileService) {}

  getSelectColDef(config?: selectColDef): ColDef {
    return {
      colId: "select",
      headerName: config?.headerName ?? "Select",
      width: 20,
      template: `
      <input
        type="checkbox"
        ng-click="recordGridController.toggleRowSelection(data)"
        ng-checked="recordGridController.rowSelected(data)"
      />`,
      suppressSorting: true,
      requiresPermission: config?.requiresPermission,
    };
  }

  getCommandColDef(config: commandColDef): ColDef {
    return {
      headerName: config.headerName,
      template: `<i class="${config.iconClass}" ng-click="$emit('command', {data, commandName:'${config.name}'})"></i>`,
      width: config.width ?? 20,
      colId: config.colId ?? config.headerName,
      requiresPermission: config.requiresPermission,
    };
  }

  getLinkColDef(config: linkColDef): ColDef {
    return {
      headerName: config.headerName,
      template: `<a ui-sref="${config.stateExpression}"><i class="${config.iconClass}"></i></a>`,
      colId: config.colId,
      width: config.width ?? 20,
      requiresPermission: config.requiresPermission,
    };
  }

  getBooleanColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultBooleanFilters()
          .getGridFilterFactory(),
      booleanValueGetterFactory()
    );
  }

  getNumberColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultNumberFilters()
          .getGridFilterFactory()
    );
  }

  getStringColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultStringFilters()
          .getGridFilterFactory()
    );
  }

  getDateColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateValueGetter
    );
  }

  getDateTimeColDef(config: simpleColDef): ColDef {
    return this.simpleColDef(
      config,
      config.filter ??
        new FilterProvider(config.field, this.$compile)
          .defaultDateFilters()
          .getGridFilterFactory(),
      dateTimeValueGetter
    );
  }

  simpleColDef(
    config: simpleColDef,
    filter?: Function,
    valueGetter?: ValueGetter
  ): ColDef {
    return {
      field: config.field,
      headerName: this.getHeader(config),
      filter,
      valueGetter: config.valueGetter ?? valueGetter,
      suppressMenu: config.suppressMenu,
      suppressSorting: config.suppressSorting,
      hide: config.hide,
      requiresPermission: config.requiresPermission,
      cellDoubleClicked: config.cellDoubleClicked,
    };
  }

  private getHeader(config: simpleColDef): string {
    return config.header ?? startCase(config.field);
  }
}
